/**
 * Booking Success Page
 * Shown after successful public booking submission
 */

import React from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Divider,
  Button,
  Grid,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  Notes as NotesIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';

const BookingSuccess = () => {
  const { orgCode } = useParams();
  const location = useLocation();
  const data = location.state || {};

  // If no booking data (direct navigation), show generic success
  if (!data.bookingId && !data.bookingRef) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Booking Confirmed
            </Typography>
            <Typography color="text.secondary" paragraph>
              Your booking has been submitted successfully. You will receive a confirmation SMS shortly.
            </Typography>
            <Button
              component={Link}
              to={`/book/${orgCode}`}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Make Another Booking
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const primaryColor = data.primaryColor || '#2c3e50';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          {/* Header with Logo */}
          <Box sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
            {data.orgLogo && (
              <Box mb={2}>
                <img
                  src={data.orgLogo}
                  alt={data.orgName}
                  style={{ maxHeight: 60, maxWidth: 180 }}
                />
              </Box>
            )}
          </Box>

          {/* Success Banner */}
          <Box
            sx={{
              bgcolor: '#28a745',
              color: 'white',
              py: 4,
              px: 3,
              textAlign: 'center',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 50, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Booking Confirmed
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.95 }}>
              Your booking has been submitted successfully. You will receive a confirmation SMS shortly.
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            {/* Booking Reference */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Booking Reference
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: primaryColor }}>
                {data.bookingRef || `#${data.bookingId}`}
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Vehicle Details */}
            <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CarIcon sx={{ mr: 1, color: primaryColor }} />
                <Typography variant="h6" fontWeight="bold">
                  Vehicle Details
                </Typography>
              </Box>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {data.vehicleReg}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {data.vehicleMake} {data.vehicleModel}
                </Typography>
                {(data.vehicleYear || data.vehicleFuelType) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {[data.vehicleYear, data.vehicleFuelType].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Paper>
            </Box>

            {/* Appointment Details */}
            <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarIcon sx={{ mr: 1, color: primaryColor }} />
                <Typography variant="h6" fontWeight="bold">
                  Appointment Details
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.bookingDate}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">
                      Time Slot
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.timeSlot}
                    </Typography>
                  </Paper>
                </Grid>
                {data.locationName && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                      <Box display="flex" alignItems="flex-start">
                        <LocationIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary', fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {data.locationName}
                          </Typography>
                          {data.locationAddress && (
                            <Typography variant="body2" color="text.secondary">
                              {data.locationAddress}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Services Required */}
            {data.services && data.services.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <BuildIcon sx={{ mr: 1, color: primaryColor }} />
                  <Typography variant="h6" fontWeight="bold">
                    Services Required
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {data.services.map((service, index) => (
                      <Chip
                        key={index}
                        label={service.name}
                        sx={{
                          bgcolor: primaryColor,
                          color: 'white',
                          fontWeight: 'medium',
                        }}
                      />
                    ))}
                  </Box>
                  {data.total > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" color="text.secondary">
                        Estimated Total: <strong>£{data.total.toFixed(2)}</strong>
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* Additional Notes */}
            {data.notes && (
              <Box sx={{ mb: 4 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <NotesIcon sx={{ mr: 1, color: primaryColor }} />
                  <Typography variant="h6" fontWeight="bold">
                    Additional Notes
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    "{data.notes}"
                  </Typography>
                </Paper>
              </Box>
            )}

            <Divider sx={{ mb: 4 }} />

            {/* Contact Info */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                If you have any questions or need assistance, please don't hesitate to contact us:
              </Typography>
              {data.locationName && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Our Address</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {data.locationName} — {data.locationAddress}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* SMS Confirmation Note */}
            <Box
              sx={{
                bgcolor: '#e3f2fd',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                mb: 3,
              }}
            >
              <Typography variant="body2" color="primary">
                A confirmation SMS has been sent to your registered mobile number.
              </Typography>
            </Box>

            {/* Thank You Message */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                We look forward to serving you. Thank you for choosing {data.orgName || 'us'}!
              </Typography>
            </Box>

            {/* Make Another Booking Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                component={Link}
                to={`/book/${orgCode}`}
                variant="outlined"
                size="large"
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  '&:hover': {
                    borderColor: primaryColor,
                    bgcolor: 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                Make Another Booking
              </Button>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              bgcolor: '#f5f5f5',
              py: 2,
              px: 3,
              textAlign: 'center',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} {data.orgName || 'Autohub Autocentres'}. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default BookingSuccess;
